import { z } from 'zod';
import { EmploymentType, requiredFieldError } from '..';
import { BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD, FINANCIAL_PRODUCT } from '../constants';
import { incomeVerificationSchema } from '../shared';
import { ApplicationStep } from '../types';
import { baseApplicationStep } from './baseApplication';

export const applicationIncomeSchema = incomeVerificationSchema.superRefine((val, ctx) => {
  const isVerificationRequired = () => {
    switch (val.financialProductId) {
      case FINANCIAL_PRODUCT.FLEX:
        return false;
      case FINANCIAL_PRODUCT.BRIGHTE_PAY:
        return (val?.applicant?.financeAmount ?? 0) > BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD;
      case FINANCIAL_PRODUCT.ELECTRIFY_2515:
        // Skip verification for cash applications
        return 'payment' in val && val?.payment?.isCashApplication;
      default:
        return 'usingOpenBanking' in val.applicant && !val.applicant.usingOpenBanking;
    }
  };

  if (val.applicant.employment) {
    switch (val.applicant.employment.type) {
      case EmploymentType.EMPLOYED:
        if (!val.applicant.employment.employed.employerBusinessName) {
          return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError(),
            path: ['applicant', 'employment', 'employed', 'employerBusinessName'],
          });
        }
        break;
      case EmploymentType.SELF_EMPLOYED:
        if (!val.applicant.employment.selfEmployed?.abn || !val.applicant.employment.selfEmployed?.entityName) {
          return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError(),
            path: ['applicant', 'employment', 'selfEmployed', 'abn'],
          });
        }
        break;
      case EmploymentType.PENSIONER:
        if (!val.applicant.employment.pensioner?.pensionType) {
          return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError(),
            path: ['applicant', 'employment', 'pensioner', 'pensionType'],
          });
        }
        break;
    }
  }

  if (isVerificationRequired()) {
    const customerDocumentIssue = {
      code: z.ZodIssueCode.custom,
      message: 'Income verification documents are required.',
      path: ['applicant', 'income'],
    };

    if (val.applicant?.income?.primary?.amount <= 0) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Amount must be greater than $0',
        path: ['applicant.income.primary.amount'],
      });
    }

    if (val.applicant.income?.isAdditionalIncome === undefined)
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredFieldError(),
        path: ['applicant', 'income', 'isAdditionalIncome'],
      });

    if (val.applicant?.income?.isAdditionalIncome && val.applicant?.income?.additional?.amount <= 0) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Amount must be greater than $0',
        path: ['applicant.income.additional.amount'],
      });
    }

    if (
      'isAdditionalIncome' in val.applicant.income &&
      val.applicant.income.isAdditionalIncome &&
      !val.applicant.income.isSkipAdditionalIncome
    ) {
      const { bankStatementToken, documentIds } = val.applicant.income.additional;
      if (!bankStatementToken && (!documentIds || documentIds?.length == 0)) {
        return ctx.addIssue(customerDocumentIssue);
      }
    }

    const { bankStatementToken, documentIds } = val.applicant.income.primary;
    if (
      !val.applicant.income.isSkipPrimaryIncome &&
      !bankStatementToken &&
      (!documentIds || documentIds?.length == 0)
    ) {
      return ctx.addIssue(customerDocumentIssue);
    }
  }
});

export const updateApplicationIncomeSchema = applicationIncomeSchema.and(baseApplicationStep).and(
  z.object({
    step: z.literal(ApplicationStep.IncomeVerification),
  }),
);
